/**
 * bark-route: how a bark is routed. Every field defaults to Auto (Viko picks: rules > learned >
 * defaults, G11). The user can pin the agent, machine, provider account and model per bark, make
 * that the default for a project, or set a global default (You › Providers › Routing). Defaults
 * are encrypted `setting` entities; the hub never sees them. Precedence for a new bark:
 * this bark's override, else the project default, else the global default, else Auto.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BarkRouteSetting,
  type BarkRouting,
  barkRouteProjectEntityId,
  isAutoRouting,
  SettingEntry,
  settingEntityId,
} from '@viko/protocol'
import { modelLabel } from '@viko/transcript-ui'
import { useBackend } from './backend.js'
import type { BrowserEntities } from './encrypted-entities.js'

type Entities = Pick<BrowserEntities, 'list' | 'write'>
const AGENT = { claude: 'Claude', codex: 'Codex' } as const
export const AUTO_ROUTE_LABEL = 'Auto · Viko picks the agent & machine'

/** Drop empty fields; `{}` means Auto. */
export function cleanRouting(r: BarkRouting | undefined | null): BarkRouting {
  const out: BarkRouting = {}
  if (r?.agent) out.agent = r.agent
  if (r?.machineId) out.machineId = r.machineId
  if (r?.accountId) out.accountId = r.accountId
  if (r?.model) out.model = r.model
  return out
}

/** "Codex · Mac · Opus 5.5"; only the parts that are pinned. Empty when everything is Auto. */
export function routeLabel(
  r: BarkRouting | undefined | null,
  machineName: (id: string) => string | undefined = () => undefined,
): string {
  const c = cleanRouting(r)
  return [
    c.agent ? AGENT[c.agent] : '',
    c.machineId ? (machineName(c.machineId) ?? 'chosen machine') : '',
    c.accountId ? 'chosen account' : '',
    c.model ? (modelLabel(c.model) ?? c.model) : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

/** The default a new bark starts from: project default, else global default, else Auto. */
export function effectiveDefault(
  globalDefault: BarkRouting | undefined,
  projectDefault: BarkRouting | undefined,
): BarkRouting {
  return cleanRouting(projectDefault ?? globalDefault)
}

async function readSetting(
  entities: Entities,
  id: string,
  name: 'barkRoute' | 'barkRouteProject',
): Promise<BarkRouting | undefined> {
  const row = (await entities.list('setting')).find((r) => r.id === id)
  const entry = SettingEntry.safeParse(row?.value)
  if (!entry.success || entry.data.name !== name) return undefined
  const v = BarkRouteSetting.safeParse(entry.data.value)
  return v.success ? cleanRouting(v.data.routing) : undefined
}

export const loadGlobalRoute = (entities: Entities) =>
  readSetting(entities, settingEntityId('barkRoute'), 'barkRoute')
export const loadProjectRoute = (entities: Entities, projectId: string) =>
  readSetting(entities, barkRouteProjectEntityId(projectId), 'barkRouteProject')

export async function saveGlobalRoute(entities: Entities, r: BarkRouting, now = Date.now()) {
  const value = BarkRouteSetting.parse({ routing: cleanRouting(r) })
  await entities.write(
    'setting',
    settingEntityId('barkRoute'),
    SettingEntry.parse({ name: 'barkRoute', value, updatedAt: now }),
  )
}

/** An explicit project default (even Auto) overrides the global one. */
export async function saveProjectRoute(
  entities: Entities,
  projectId: string,
  r: BarkRouting,
  now = Date.now(),
) {
  const value = BarkRouteSetting.parse({ routing: cleanRouting(r), projectId })
  await entities.write(
    'setting',
    barkRouteProjectEntityId(projectId),
    SettingEntry.parse({ name: 'barkRouteProject', value, updatedAt: now }),
  )
}

/** Global and project defaults for a project's bark box, plus savers that refresh them. */
export function useBarkRoute(projectId: string | undefined) {
  const b = useBackend()
  const qc = useQueryClient()
  const entities = b.entities
  const account = b.artifactAccess?.accountId
  const key = ['bark-route', account, projectId] as const
  const q = useQuery({
    queryKey: key,
    queryFn: async () => ({
      global: entities ? await loadGlobalRoute(entities) : undefined,
      project: entities && projectId ? await loadProjectRoute(entities, projectId) : undefined,
    }),
    enabled: !!entities,
    retry: false,
  })
  const refresh = () => qc.invalidateQueries({ queryKey: ['bark-route', account] })
  return {
    ready: !entities || q.isSuccess,
    global: q.data?.global,
    project: q.data?.project,
    /** What a new bark starts from. */
    value: effectiveDefault(q.data?.global, q.data?.project),
    saveProject: async (r: BarkRouting) => {
      if (!entities || !projectId) throw new Error('Encrypted account locked')
      await saveProjectRoute(entities, projectId, r)
      await refresh()
    },
    saveGlobal: async (r: BarkRouting) => {
      if (!entities) throw new Error('Encrypted account locked')
      await saveGlobalRoute(entities, r)
      await refresh()
    },
  }
}

export { isAutoRouting }
